import { UAParser } from "ua-parser-js";

const sessionId = new Date().getTime();

const getBaseDeviceProperties = () => {
  const device = new UAParser();

  const {
    device: { vendor },
    os: { name, version },
  } = device.getResult();

  return {
    osName: name,
    osVersion: version,
    platform: vendor,
    appVersion: "v2",
  };
};

const getBaseEventProperties = (
  eventName: string,
  additionalProperties: IEventAdditionalProperties | undefined,
) => ({
  event_name: eventName,
  datetime: new Date().toISOString(),
  url: window.location.pathname,
  ...(additionalProperties !== undefined && additionalProperties),
});

const getBaseEvent = (
  eventName: string,
  additionalProperties: IEventAdditionalProperties | undefined,
) => ({
  sessionId,
  ...getBaseDeviceProperties(),
  eventProperties: {
    ...getBaseEventProperties(eventName, additionalProperties),
  },
});

const getEventProperties = ({
  eventName,
  additionalProperties,
}: {
  eventName: string;
  additionalProperties?: IEventAdditionalProperties;
}) => {
  switch (eventName) {
    case "onboarding.collect.consent-form.privacy-email.submit": {
      return {
        eventType: "submit privacy contact email address",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.collect.consent-form.privacy-policy-url.submit": {
      return {
        eventType: "submit privacy policy url",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.collect.navigate-back":
      return {
        eventType: "navigate back during onboarding",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "onboarding.collect.klaviyo.access-settings-url": {
      return {
        eventType: "access klaviyo key settings url",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.collect.klaviyo.complete-setup": {
      return {
        eventType: "complete klaviyo setup",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.collect.klaviyo.go-to-private-key": {
      return {
        eventType: "navigate to klaviyo private key",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.collect.access-shopify-listing": {
      return {
        eventType: "access shopify listing from profile",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.collect.klaviyo.submit-private-key": {
      return {
        eventType: "submit klaviyo private key",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.collect.optimize-checkout.settings-url": {
      return {
        eventType: "access shopify checkout settings url",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.collect.optimize-checkout.copy-widget-code": {
      return {
        eventType: "copy widget code",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.collect.optimize-checkout.add-widget-code": {
      return {
        eventType: "add widget code in shopify",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.collect.disable-shopify-consent.complete": {
      return {
        eventType: "disable shopify consent",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "signup.start": {
      return {
        eventType: "start sign up",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "general.login":
      return {
        eventType: "log in",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "company-selector.back-to-signin":
      return {
        eventType: "return to sign in",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "control-panel.email.dashboard.expand-stats": {
      return {
        eventType: "expand dashboard summary dropdown",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "component.image-preview-carousel.previous":
      return {
        eventType: "image preview carousel - previous",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "component.image-preview-carousel.next":
      return {
        eventType: "image preview carousel - next",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "support.mailto": {
      return {
        eventType: "email support",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.signup.submit": {
      return {
        eventType: "submit sign up",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.signup.existing_user": {
      return {
        eventType: "login to existing account",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "onboarding.start": {
      return {
        eventType: "start onboarding",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.email.compliance-report.view": {
      return {
        eventType: "view compliance report",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.email.compliance-report.review": {
      return {
        eventType: "review compliance report",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.widget-settings.preview.view-modal": {
      return {
        eventType: "open preview marketing modal text",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.collect.integration-settings.klaviyo-key.view": {
      return {
        eventType: "access integrations settings - klaviyo api key",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.collect.integration-settings.klaviyo-key.update": {
      return {
        eventType: "update klaviyo private key",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.email.integration-settings.klaviyo-lists.view": {
      return {
        eventType: "access integrations settings - klaviyo lists",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.email.integration-settings.klaviyo-lists.open-list": {
      return {
        eventType: "open list modal",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.collect.integration-settings.shopify-app.view": {
      return {
        eventType: "access integrations settings - shopify app",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.collect.widget-settings.email-installation.view": {
      return {
        eventType: "access widget settings - email installation",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.collect.widget-settings.privacy-details.view": {
      return {
        eventType: "access widget settings - privacy details",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.collect.widget-settings.privacy-details.update-url": {
      return {
        eventType: "update privacy url",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.collect.widget-settings.privacy-details.update-email": {
      return {
        eventType: "update privacy email",
        ...getBaseEvent(eventName, additionalProperties),
      };
    }
    case "control-panel.billing.access-approve-subscription":
      return {
        eventType: "access subscription approval",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "control-panel.billing.approve-subscription":
      return {
        eventType: "approve subscription charge",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "control-panel.billing.decline-subscription":
      return {
        eventType: "decline subscription charge",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "control-panel.billing.access-external-billing":
      return {
        eventType: "access shopify billing",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "control-panel.billing.request-cancel-subscription":
      return {
        eventType: "request cancel subscription",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "control-panel.billing.confirm-cancel-subscription":
      return {
        eventType: "confirm cancel subscription",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "control-panel.billing.reprocess-subscription":
      return {
        eventType: "reprocess declined subscription",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "control-panel.billing.notification-link":
      return {
        eventType: "access billing screen from banner notification",
        ...getBaseEvent(eventName, additionalProperties),
      };
    case "control-panel.billing.notification-dismiss":
      return {
        eventType: "dismiss billing banner notification",
        ...getBaseEvent(eventName, additionalProperties),
      };
    default: {
      console.warn("eventManager/NoMatchingEventFound", eventName);
      return undefined;
    }
  }
};

export default getEventProperties;
